import { Calendar, ChevronDown, ChevronUp, CreditCard, Target, TrendingUp } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import type { CustomerAnalyticsQuery } from "@/gql/graphql";

interface CustomerFinancialInsightsCardProps {
  analytics?: NonNullable<CustomerAnalyticsQuery["customerAnalytics"]>;
  loading?: boolean;
}

export function CustomerFinancialInsightsCard({
  analytics,
  loading = false,
}: CustomerFinancialInsightsCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (loading || !analytics) {
    return <FinancialInsightsSkeleton />;
  }

  const { lifetimeValue, servicePreferences } = analytics;

  const getTrendIcon = (_direction: string, percent?: number | null) => {
    if (!percent) return null;

    const isPositive = percent > 0;
    return (
      <div
        className={`flex items-center gap-1 text-sm ${
          isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
        }`}
      >
        <TrendingUp className={`w-3 h-3 ${!isPositive ? "rotate-180" : ""}`} />
        <span>
          {isPositive ? "+" : ""}
          {percent.toFixed(1)}%
        </span>
      </div>
    );
  };

  const getUsageColor = (frequency: string) => {
    switch (frequency) {
      case "frequent":
        return "bg-green-100 text-green-900 border-green-300 dark:bg-green-950 dark:text-green-100";
      case "regular":
        return "bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-100";
      case "occasional":
        return "bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-950 dark:text-yellow-100";
      case "rare":
        return "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100";
      default:
        return "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100";
    }
  };

  // Calculate some insights
  const _topSpendingService = servicePreferences.reduce(
    (max, service) => (service.totalSpent > max.totalSpent ? service : max),
    servicePreferences[0]
  );

  const premiumServices = servicePreferences.filter(
    (s) => s.avgPrice > lifetimeValue.avgSpendPerVisit
  );

  const recentlyBookedServices = servicePreferences
    .filter((s) => s.daysSinceLastBooked <= 30)
    .sort((a, b) => a.daysSinceLastBooked - b.daysSinceLastBooked);

  const formatDaysAgo = (days: number) => {
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30)
      return `${Math.round(days / 7)} week${Math.round(days / 7) !== 1 ? "s" : ""} ago`;
    return `${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? "s" : ""} ago`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-600 dark:text-green-400" />
            Financial Insights
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground hover:text-foreground"
          >
            {isExpanded ? (
              <>
                <span className="mr-1">Collapse</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span className="mr-1">Expand</span>
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Collapsed State - Key insights */}
        {!isExpanded ? (
          <>
            {/* Spending Trend */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-900 dark:text-green-100">
                  Average Spend Per Visit
                </span>
                {getTrendIcon(
                  lifetimeValue.spendingTrendDirection,
                  lifetimeValue.spendingTrendPercent
                )}
              </div>
              <div className="text-xl font-bold text-green-600 dark:text-green-400">
                {<PriceDisplay amount={lifetimeValue.avgSpendPerVisit} />}
              </div>
              <div className="text-xs text-green-700 dark:text-green-300">vs previous visits</div>
            </div>

            {/* Upselling Opportunities */}
            <div className="bg-muted p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Opportunities</span>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                {premiumServices.length > 0 ? (
                  <>Enjoys premium services • {premiumServices.length} services above average</>
                ) : (
                  <>Potential for upselling to premium services</>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Expanded State - Full financial analysis */}

            {/* Spending Analysis */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Spending Analysis
              </h4>
              <div className="border rounded-lg p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Recent Trend</div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold">
                        {lifetimeValue.spendingTrendPercent ? (
                          <>
                            {lifetimeValue.spendingTrendPercent > 0 ? "+" : ""}
                            {lifetimeValue.spendingTrendPercent.toFixed(1)}%
                          </>
                        ) : (
                          "Stable"
                        )}
                      </span>
                      {getTrendIcon(
                        lifetimeValue.spendingTrendDirection,
                        lifetimeValue.spendingTrendPercent
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">vs previous visits</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Average Visit</div>
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {<PriceDisplay amount={lifetimeValue.avgSpendPerVisit} />}
                    </div>
                    <div className="text-xs text-muted-foreground">per appointment</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Total Value</div>
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {<PriceDisplay amount={lifetimeValue.lifetimeValue} />}
                    </div>
                    <div className="text-xs text-muted-foreground">lifetime</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Spending Services */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Top Spending Categories
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {servicePreferences.slice(0, 4).map((service, index) => (
                  <div key={service.serviceName} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            index === 0
                              ? "bg-emerald-500"
                              : index === 1
                                ? "bg-green-500"
                                : index === 2
                                  ? "bg-lime-500"
                                  : "bg-gray-400"
                          }`}
                        />
                        <span className="font-medium text-sm">{service.serviceName}</span>
                      </div>
                      <Badge variant="outline" className={getUsageColor(service.usageFrequency)}>
                        {service.usageFrequency}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total Spent:</span>
                        <span className="font-medium text-green-600 dark:text-green-400">
                          {<PriceDisplay amount={service.totalSpent} />}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Average Price:</span>
                        <span>{<PriceDisplay amount={service.avgPrice} />}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Share of Spending:</span>
                        <span>
                          {((service.totalSpent / lifetimeValue.lifetimeValue) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Service Analysis */}
            {premiumServices.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2 text-amber-900 dark:text-amber-100">
                  <Target className="w-4 h-4" />
                  Premium Service Preference
                </h4>
                <div className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                  This customer enjoys premium services and typically spends above their average.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {premiumServices.slice(0, 2).map((service) => (
                    <div
                      key={service.serviceName}
                      className="bg-white dark:bg-amber-900/20 p-3 rounded border border-amber-200 dark:border-amber-800"
                    >
                      <div className="font-medium text-sm">{service.serviceName}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {<PriceDisplay amount={service.avgPrice} />} avg • {service.bookingCount}{" "}
                        bookings
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {recentlyBookedServices.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Recent Service Activity
                </h4>
                <div className="space-y-2">
                  {recentlyBookedServices.slice(0, 3).map((service) => (
                    <div
                      key={service.serviceName}
                      className="flex items-center justify-between p-2 border rounded"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-sm font-medium">{service.serviceName}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{<PriceDisplay amount={service.totalSpent} />} total</span>
                        <span>{formatDaysAgo(service.daysSinceLastBooked)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FinancialInsightsSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-40" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/2" />
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
